import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { trackerKeys } from './keys'
import type { TrackerPresentationResponse, TrackerTicketDetail, TrackerTicketListResponse } from '@/types/tracker'

// asUser must live in the query key: see useTickets for why (a lensed and an
// unlensed view of the same params must not share a cache entry).
export function useTrackerTickets(params: Record<string, unknown> = {}, asUser?: string) {
  return useQuery({
    queryKey: [...trackerKeys.list(params), asUser ?? null],
    queryFn: () => api<TrackerTicketListResponse>('/support-tickets/tracker', {
      query: asUser ? { ...params, asUserId: asUser } : params,
    }),
  })
}

// Detail companion to useTrackerTickets: full ticket + thread for one
// promoted ticket. asUser threads through exactly like useTrackerTickets —
// see that hook's comment for why it must live in both the key and the request.
export function useTrackerTicket(id: string, asUser?: string) {
  return useQuery({
    queryKey: [...trackerKeys.detail(id), asUser ?? null],
    queryFn: () => api<TrackerTicketDetail>(`/support-tickets/tracker/${id}`, {
      query: asUser ? { asUserId: asUser } : undefined,
    }),
  })
}

// Effective category list for a company — the same list
// supportTicket.service#effectiveTrackerCategories validates promote/patch
// against, so the promote dialog and the tracker panel's category select can
// never drift from what the server will actually accept. Left on the app's
// default retry/backoff (no `retry: false` override here) like every other
// query in this file — a lone per-query override isn't worth the special
// case for what's ultimately a small reference-list fetch.
export function useTrackerCategories(companyId?: string, asUser?: string) {
  return useQuery({
    queryKey: trackerKeys.categories(companyId, asUser),
    queryFn: () =>
      api<{ categories: string[] }>('/support-tickets/tracker/categories', {
        query: asUser ? { companyId, asUserId: asUser } : { companyId },
      }).then(r => r.categories),
    enabled: Boolean(companyId),
  })
}

// Full-screen client-call deck — GET /support-tickets/tracker/presentation.
// Deliberately the ONLY query PresentationView makes: the endpoint already
// returns the client-safe set (internal/system messages filtered server-side,
// two most recent replies per ticket) so there is nothing left for this hook
// to fetch per-card — no message thread, no ticket detail. asUser and
// includeClosed both thread through the key and the request exactly like
// useTrackerTickets: a lensed and unlensed view, or an open and closed-
// inclusive view, must never share a cache entry.
export function useTrackerPresentation(
  companyId?: string,
  options: { includeClosed?: boolean } = {},
  asUser?: string,
) {
  const includeClosed = options.includeClosed ?? false
  return useQuery({
    queryKey: trackerKeys.presentation(companyId, includeClosed, asUser),
    queryFn: () => {
      const query: Record<string, unknown> = {}
      if (companyId) query.companyId = companyId
      if (includeClosed) query.includeClosed = 'true'
      if (asUser) query.asUserId = asUser
      return api<TrackerPresentationResponse>('/support-tickets/tracker/presentation', { query })
    },
    // An admin unlensed with neither param would just 400 (companyId is
    // required server-side) — no point firing that request at all.
    enabled: Boolean(companyId) || Boolean(asUser),
  })
}

interface ApiError {
  statusCode?: number
  status?: number
}

// 404 from the flag middleware means "not enabled for this company" — that is
// the contract, so the probe treats it as data, not an error. `asUser` must
// be threaded through exactly like useTrackerTickets: a staffer lensing a
// flagless company's user must get their own 404, not the acting admin's
// server-side bypass — see supportTicket.service's lensed flag check.
export function useTrackerEnabled(asUser?: string) {
  return useQuery({
    queryKey: [...trackerKeys.all, 'enabled', asUser ?? null],
    queryFn: () =>
      api('/support-tickets/tracker', { query: asUser ? { limit: 1, asUserId: asUser } : { limit: 1 } }).then(
        () => true,
        (e: ApiError) => (e?.statusCode === 404 || e?.status === 404 ? false : Promise.reject(e)),
      ),
    staleTime: 10 * 60_000,
  })
}
